/**
 * components/landing/platform/sections/Philosophy.jsx — §3.2 Platform Philosophy.
 *
 * The "broken ecosystem → unified" moment.  On the left, a chaotic
 * grid of disconnected vendor logos.  On the right, one calm unified
 * Kalki Ai Cloud surface.
 */

import React from 'react'
import { AlertTriangle, CheckCircle2, ArrowRight, Bot, LineChart, GraduationCap, CreditCard, PieChart, UsersRound, MessageSquare } from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'

const SEPARATE_TOOLS = [
  { i: UsersRound,    n: 'CRM'         },
  { i: LineChart,     n: 'Trading'     },
  { i: GraduationCap, n: 'LMS'         },
  { i: CreditCard,    n: 'Billing'     },
  { i: PieChart,      n: 'Analytics'   },
  { i: Bot,           n: 'AI'          },
  { i: MessageSquare, n: 'Support'     },
]

export default function Philosophy() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-12">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Platform Philosophy</Eyebrow>
          <SectionHeading
            align="center"
            title={<>Not a stack of tools. <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">One operating system.</span></>}
            subtitle="Modern businesses are drowning in disconnected software — CRM, trading, LMS, billing, analytics, AI, support. Kalki Ai Cloud replaces that fragmented stack with a single, integrated platform designed specifically for AI-powered trading businesses."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Broken ecosystem */}
          <div className="relative rounded-3xl border border-rose-500/20 bg-rose-500/[0.03] p-6 md:p-8 overflow-hidden">
            <div className="pointer-events-none absolute inset-0 opacity-[0.4]"
              style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(239,68,68,0.14), transparent 70%)' }} />
            <div className="relative flex items-center gap-2 mb-4">
              <AlertTriangle className="h-4 w-4 text-rose-400" />
              <span className="text-[11px] uppercase tracking-wider text-rose-400 font-semibold">Today · fragmented</span>
            </div>
            <h3 className="text-2xl font-semibold text-white mb-4">Seven vendors. Seven contracts. Seven brains.</h3>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {SEPARATE_TOOLS.map((t) => (
                <div key={t.n} className="flex flex-col items-center gap-1.5 rounded-lg border border-rose-500/20 bg-rose-500/[0.06] py-3">
                  <t.i className="h-4 w-4 text-rose-300" />
                  <span className="text-[11px] font-semibold text-rose-100">{t.n}</span>
                </div>
              ))}
            </div>
            <ul className="grid grid-cols-1 gap-1.5 text-sm text-zinc-300">
              {[
                'Fragile integrations between tools',
                'Users retrained per vendor',
                'Redundant data silos',
                'Duplicate spend, missed insights',
              ].map((l, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-400" /> {l}
                </li>
              ))}
            </ul>
          </div>

          {/* Unified ecosystem */}
          <div className="relative rounded-3xl border border-brand-gold/30 bg-brand-gold/[0.04] p-6 md:p-8 overflow-hidden">
            <div className="pointer-events-none absolute inset-0 opacity-[0.4]"
              style={{ background: 'radial-gradient(60% 40% at 80% 0%, rgba(255,193,7,0.18), transparent 70%)' }} />
            <div className="relative flex items-center gap-2 mb-4">
              <CheckCircle2 className="h-4 w-4 text-brand-gold" />
              <span className="text-[11px] uppercase tracking-wider text-brand-gold font-semibold">Kalki Ai Cloud · unified</span>
            </div>
            <h3 className="text-2xl font-semibold text-white mb-4">One login. One surface. One operating system.</h3>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { i: Bot,           n: 'AI Mentor' },
                { i: LineChart,     n: 'Terminal'  },
                { i: GraduationCap, n: 'Academy'   },
                { i: CreditCard,    n: 'Billing'   },
                { i: PieChart,      n: 'Analytics' },
                { i: UsersRound,    n: 'CRM'       },
              ].map((t) => (
                <div key={t.n} className="flex flex-col items-center gap-1.5 rounded-lg border border-brand-gold/25 bg-brand-gold/[0.08] py-3">
                  <t.i className="h-4 w-4 text-brand-gold" />
                  <span className="text-[11px] font-semibold text-white">{t.n}</span>
                </div>
              ))}
            </div>
            <ul className="grid grid-cols-1 gap-1.5 text-sm text-zinc-100">
              {[
                'Built-in cross-module data flow',
                'Consistent UI — one training program',
                'One source of truth, one bill',
                'AI wired into every workflow',
              ].map((l, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-gold" /> {l}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* transition arrow */}
        <div className="flex items-center justify-center gap-3 text-xs text-zinc-500">
          <span className="h-px w-16 bg-white/10" />
          <ArrowRight className="h-3.5 w-3.5" />
          <span>Everything below is what Kalki Ai Cloud already brings, in one login.</span>
          <ArrowRight className="h-3.5 w-3.5" />
          <span className="h-px w-16 bg-white/10" />
        </div>
      </Container>
    </section>
  )
}
